
/* ==========================================
   TIMI INSPO AUTOFILL v1.9.1
   Make(r) Your Own — shared across products

   - Inspiration dropdown + Surprise Me
   - Delegated Surprise Me click handling
   - Supports Zepto embroidery mode switching
   - Natural multiline phrase formatting
   - Compact single-line charms and tags
   - Supports apparel placement options
   - Respects text field character limits
   - NO automatic font or design resizing

   Zepto controls text sizing, placement,
   embroidery boundaries and pricing.
   ========================================== */

(() => {
  'use strict';

  if (window.__timiInspoAutofillV191) return;
  window.__timiInspoAutofillV191 = true;

  const STYLE_ID = 'timi-inspo-autofill-styles';
  const MAX_LINE = 15;
  const MAX_LINES = 3;
  const MAX_TOTAL = 45;

  let requestNumber = 0;
  let scheduled = false;

  const PREFERRED_BREAKS = {
    'knits & giggles': ['knits &', 'giggles'],
    'always knitting': ['always', 'knitting'],
    'rhinebeck made me do it': [
      'rhinebeck', 'made me do it'
    ],
    'anti social knitting club': [
      'anti social', 'knitting club'
    ],
    'spiritually on gauge': [
      'spiritually', 'on gauge'
    ],
    'catch floats, not feelings.': [
      'catch floats,', 'not feelings.'
    ],
    'emotional support wip': [
      'emotional', 'support wip'
    ],
    'ask me about my sweater': [
      'ask me about', 'my sweater'
    ],
    'mentally at rhinebeck': [
      'mentally', 'at rhinebeck'
    ],
    'resting stitch face': [
      'resting', 'stitch face'
    ],
    'skein character energy': [
      'skein character', 'energy'
    ],
    'slow fashion club': [
      'slow fashion', 'club'
    ],
    'thanks, i made it': [
      'thanks,', 'i made it'
    ],
    'just one more row': [
      'just one', 'more row'
    ],
    'knit just got real': [
      'knit just', 'got real'
    ],
    'nothing but knit': [
      'nothing but', 'knit'
    ],
    'work in progress': [
      'work in', 'progress'
    ],
    'mohair, don’t care.': [
      'mohair,', 'don’t care.'
    ],
    'socks, socks, socks.': [
      'socks, socks,', 'socks.'
    ],
    'i brought my knitting (just in case)': [
      'i brought my',
      'knitting',
      '(just in case)'
    ],
    'i brought my crochet (just in case)': [
      'i brought my',
      'crochet',
      '(just in case)'
    ],
    'i brought my yarn (just in case)': [
      'i brought my',
      'yarn',
      '(just in case)'
    ]
  };

  /* ========================================
     HELPERS AND MODE DETECTION
     ======================================== */

  function clean(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeMode(value) {
    return clean(value)
      .replace(/\(s\)/g, 's')
      .replace(/\s*\+\s*/g, ' + ');
  }

  function getProduct(element) {
    return element?.closest(
      '.product-personalizer'
    ) || null;
  }

  function getModeGroup(product) {
    const groups = new Map();

    product?.querySelectorAll(
      '.pplr-drop-item'
    ).forEach(button => {
      const parent = button.parentElement;

      if (!parent) return;

      if (!groups.has(parent)) {
        groups.set(parent, []);
      }

      groups.get(parent).push(button);
    });

    const candidates = [
      ...groups.entries()
    ].filter(([, buttons]) => {
      const values = buttons.map(button =>
        normalizeMode(
          button.getAttribute('data-value') ||
          button.textContent
        )
      );

      return (
        values.includes('text only') &&
        (
          values.includes('icons only') ||
          values.includes('icon only')
        )
      );
    });

    return candidates.sort(
      (a, b) => b[1].length - a[1].length
    )[0] || null;
  }

  function isSelected(button) {
    if (
      button.classList.contains('active') ||
      button.classList.contains('selected') ||
      button.classList.contains('pplr_active') ||
      button.classList.contains('pplr_selected') ||
      button.getAttribute('aria-pressed') === 'true'
    ) {
      return true;
    }

    const rgb = getComputedStyle(button)
      .backgroundColor
      .match(/[\d.]+/g)
      ?.slice(0, 3)
      .map(Number);

    return (
      rgb?.length === 3 &&
      Math.abs(rgb[0] - 247) < 12 &&
      Math.abs(rgb[1] - 188) < 12 &&
      Math.abs(rgb[2] - 208) < 12
    );
  }

  function currentMode(product) {
    const group = getModeGroup(product);

    if (!group) return '';

    const active = group[1].find(isSelected);

    return normalizeMode(
      active?.getAttribute('data-value') ||
      active?.textContent ||
      ''
    );
  }

  function isVisible(element) {
    if (!element || !element.isConnected) {
      return false;
    }

    if (
      element.hidden ||
      element.closest('[hidden]')
    ) {
      return false;
    }

    if (!element.getClientRects().length) {
      return false;
    }

    const style = getComputedStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  }

  function getTextField(product) {
    const fields = [
      ...(product?.querySelectorAll(
        'textarea.pplr_text'
      ) || [])
    ];

    return fields.find(isVisible) ||
      fields[0] ||
      null;
  }

  function textMode(product) {
    if (!product) return false;

    const group = getModeGroup(product);

    if (!group) {
      return !!getTextField(product);
    }

    return [
      'text only',
      'text + icons',
      'text + icon'
    ].includes(currentMode(product));
  }

  function isCompact(product) {
    const group = getModeGroup(product);
    const field = getTextField(product);

    if (
      field?.maxLength > 0 &&
      field.maxLength <= 16
    ) {
      return true;
    }

    if (!group) return false;

    const names = group[1].map(button =>
      normalizeMode(
        button.getAttribute('data-value') ||
        button.textContent
      )
    );

    return (
      names.includes('text only') &&
      (
        names.includes('icons only') ||
        names.includes('icon only')
      ) &&
      !names.includes('timi designs') &&
      !names.includes('upload')
    );
  }

  function textLimit(product) {
    const field = getTextField(product);

    if (field?.maxLength > 0) {
      return field.maxLength;
    }

    return isCompact(product) ? 16 : MAX_TOTAL;
  }

  function isMultiline(product) {
    if (isCompact(product)) return false;

    const field = getTextField(product);

    if (!field) return false;

    const wrapperText = clean(
      field.closest('.pplr-wrapper')
        ?.textContent || ''
    );

    return (
      wrapperText.includes('3 lines') ||
      wrapperText.includes('15 per line') ||
      wrapperText.includes('3 lines × 15') ||
      textLimit(product) === 45
    );
  }

  /* ========================================
     FIND INSPIRATION DROPDOWN
     ======================================== */

  function isInspoSelect(select) {
    if (
      !(select instanceof HTMLSelectElement) ||
      !select.matches('select.pplr_select')
    ) {
      return false;
    }

    const matchesPrompt = value =>
      /need some inspo/i.test(
        String(value || '')
      );

    if (
      [...select.options].some(option =>
        matchesPrompt(option.textContent)
      )
    ) {
      return true;
    }

    const wrapper = select.closest(
      '.pplr-wrapper'
    );

    const label = wrapper?.querySelector(
      '.pplrlabel'
    );

    return matchesPrompt(
      label?.textContent
    );
  }

  function findLiveInspoSelect(product, button) {
    if (!product) return null;

    const selects = [
      ...product.querySelectorAll(
        'select.pplr_select'
      )
    ].filter(select =>
      select.isConnected &&
      isInspoSelect(select)
    );

    if (!selects.length) return null;

    // Prefer the dropdown sharing the
    // current button's parent.
    const sameHost = selects.find(select =>
      select.parentElement === button?.parentElement
    );

    if (sameHost) return sameHost;

    // Otherwise use the currently visible one.
    return selects.find(isVisible) ||
      selects[0] ||
      null;
  }

  /* ========================================
     PHRASE FORMATTING
     ======================================== */

  function charCount(text) {
    return [...String(text)].length;
  }

  function normalizePhrase(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function validLayout(lines, limit) {
    return (
      lines.length > 0 &&
      lines.length <= MAX_LINES &&
      lines.every(line =>
        line &&
        charCount(line) <= MAX_LINE
      ) &&
      charCount(lines.join('\n')) <= limit
    );
  }

  function awkwardStart(word) {
    return [
      '&', 'and', 'or', 'but', 'not', 'of'
    ].includes(clean(word));
  }

  function awkwardEnd(word) {
    return [
      'a', 'an', 'the', 'my', 'your',
      'our', 'to', 'for', 'with',
      'in', 'at', 'on'
    ].includes(clean(word));
  }

  function scoreLayout(lines) {
    const lengths = lines.map(charCount);

    let score =
      Math.max(...lengths) -
      Math.min(...lengths) +
      (lines.length - 1) * 2;

    lines.forEach((line, index) => {
      const words = line.split(' ');

      if (
        index > 0 &&
        awkwardStart(words[0])
      ) {
        score += 35;
      }

      if (
        index < lines.length - 1 &&
        awkwardEnd(words.at(-1))
      ) {
        score += 18;
      }

      if (
        lines.length > 1 &&
        charCount(line) <= 3
      ) {
        score += 12;
      }
    });

    return score;
  }

  function generateLayouts(words, limit) {
    const results = [];

    function build(index, lines) {
      if (index === words.length) {
        if (validLayout(lines, limit)) {
          results.push([...lines]);
        }

        return;
      }

      if (lines.length >= MAX_LINES) {
        return;
      }

      let current = '';

      for (
        let end = index;
        end < words.length;
        end++
      ) {
        current = current
          ? current + ' ' + words[end]
          : words[end];

        if (
          charCount(current) > MAX_LINE
        ) {
          break;
        }

        build(
          end + 1,
          [...lines, current]
        );
      }
    }

    build(0, []);
    return results;
  }

  function formatMultiline(value, limit) {
    const phrase = normalizePhrase(value);

    if (
      !phrase ||
      charCount(phrase) > limit
    ) {
      return null;
    }

    const preferred =
      PREFERRED_BREAKS[phrase.toLowerCase()];

    if (
      preferred &&
      validLayout(preferred, limit)
    ) {
      return preferred.join('\n');
    }

    if (charCount(phrase) <= 12) {
      return phrase;
    }

    const layouts = generateLayouts(
      phrase.split(' '),
      limit
    );

    if (!layouts.length) {
      return null;
    }

    const multiline = layouts.filter(
      lines => lines.length > 1
    );

    const candidates =
      multiline.length
        ? multiline
        : layouts;

    candidates.sort(
      (a, b) =>
        scoreLayout(a) -
        scoreLayout(b)
    );

    return candidates[0].join('\n');
  }

  function formatForProduct(value, product) {
    const phrase = normalizePhrase(value);
    const limit = textLimit(product);

    if (
      !phrase ||
      charCount(phrase) > limit
    ) {
      return null;
    }

    if (isMultiline(product)) {
      return formatMultiline(
        phrase,
        Math.min(limit, MAX_TOTAL)
      );
    }

    return phrase;
  }

  function getPhrases(select) {
    const product = getProduct(select);

    return [...select.options]
      .filter(option =>
        option.value.trim()
      )
      .map(option => ({
        value: option.value,
        formatted: formatForProduct(
          option.value,
          product
        )
      }))
      .filter(item => item.formatted);
  }

  /* ========================================
     ZEPTO TEXT UPDATES
     ======================================== */

  function openTextTab(product) {
    const tab = [
      ...product.querySelectorAll(
        '.pplr_tab_wrapper .pplr_tab_index'
      )
    ].find(element =>
      clean(element.textContent) === 'text'
    );

    if (
      tab &&
      !tab.classList.contains('pplr_active')
    ) {
      tab.click();
    }
  }

  function setTextValue(field, value) {
    const setter =
      Object.getOwnPropertyDescriptor(
        HTMLTextAreaElement.prototype,
        'value'
      )?.set;

    if (setter) {
      setter.call(field, value);
    } else {
      field.value = value;
    }

    field.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    field.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  function getMessage(host) {
    let message = host.querySelector(
      ':scope > .timi-inspo-message'
    );

    if (!message) {
      message = document.createElement('div');
      message.className = 'timi-inspo-message';

      message.setAttribute(
        'aria-live',
        'polite'
      );

      message.hidden = true;
      host.appendChild(message);
    }

    return message;
  }

  function showMessage(host, text = '') {
    if (!host || !host.isConnected) return;

    const message = getMessage(host);

    if (message.textContent !== text) {
      message.textContent = text;
    }

    message.hidden = !text;
  }

  /* ========================================
     AUTOFILL
     ======================================== */

  function fillText(select, phrase) {
    const host = select.parentElement;
    const product = getProduct(select);

    if (
      !host ||
      !product ||
      !select.isConnected
    ) {
      return;
    }

    const formatted = formatForProduct(
      phrase,
      product
    );

    if (!formatted) {
      showMessage(
        host,
        'This phrase does not fit the ' +
        'embroidery text limits. ' +
        'Please choose another.'
      );
      return;
    }

    showMessage(host);
    openTextTab(product);

    const request = ++requestNumber;

    // Always find the CURRENT visible Zepto
    // text field after an embroidery mode switch.
    function tryFill(attempt) {
      if (
        request !== requestNumber ||
        !product.isConnected
      ) {
        return;
      }

      const fields = [
        ...product.querySelectorAll(
          'textarea.pplr_text'
        )
      ];

      const field = fields.find(isVisible);

      if (!field) {
        if (attempt < 6) {
          window.setTimeout(
            () => tryFill(attempt + 1),
            100
          );
        } else {
          showMessage(
            host,
            'Could not find the Custom Text field.'
          );
        }

        return;
      }

      setTextValue(field, formatted);
    }

    window.setTimeout(
      () => tryFill(0),
      100
    );
  }

  /* ========================================
     SURPRISE ME
     ======================================== */

  function surpriseMe(select) {
    if (!select || !select.isConnected) {
      return;
    }

    const host = select.parentElement;
    const phrases = getPhrases(select);

    if (!phrases.length) {
      showMessage(
        host,
        'No inspiration phrases fit ' +
        'the embroidery limits.'
      );
      return;
    }

    const alternatives = phrases.filter(
      item => item.value !== select.value
    );

    const pool = alternatives.length
      ? alternatives
      : phrases;

    const choice = pool[
      Math.floor(Math.random() * pool.length)
    ];

    // Update the native dropdown.
    select.value = choice.value;

    // Explicitly call our autofill rather than
    // relying solely on Zepto's change handlers.
    fillText(select, choice.value);

    // Notify other scripts that the dropdown
    // selection has changed.
    select.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  /* ========================================
     STYLING
     ======================================== */

  function injectStyles() {
    let style = document.getElementById(
      STYLE_ID
    );

    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }

    style.textContent = `
      .timi-inspo-hidden {
        display: none !important;
      }

      .timi-inspo-ready {
        display: grid !important;
        grid-template-columns:
          minmax(0, 1fr) auto !important;
        align-items: center;
        column-gap: 7px;
        row-gap: 4px;
      }

      .timi-inspo-ready > .pplrlabel {
        grid-column: 1 / -1;
      }

      .timi-inspo-ready
      > select.timi-inspo-select {
        width: 100% !important;
        min-width: 0 !important;
        max-width: 100% !important;
        margin: 0 !important;
        box-sizing: border-box;
      }

      .timi-surprise-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        min-height: 35px;
        padding: 7px 11px;
        margin: 0;

        border: 1px solid #8f182f;
        border-radius: 7px;

        background: #fff1f5;
        color: #8f182f;

        font-family: inherit;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;

        transition:
          background-color .15s ease,
          border-color .15s ease;
      }

      .timi-surprise-button:hover {
        background: #f7bcd0;
      }

      .timi-surprise-button:focus-visible {
        outline: 2px solid #8f182f;
        outline-offset: 2px;
      }

      .timi-surprise-button[hidden] {
        display: none !important;
      }

      .timi-inspo-message {
        grid-column: 1 / -1;
        color: #8f182f;
        font-family: inherit;
        font-size: 11px;
        line-height: 1.4;
        margin-top: 3px;
      }

      .timi-inspo-message[hidden] {
        display: none !important;
      }

      @media (max-width: 749px) {
        .timi-inspo-ready {
          column-gap: 5px;
        }

        .timi-surprise-button {
          min-height: 34px;
          padding: 6px 8px;
          font-size: 10px;
        }
      }
    `;
  }

  /* ========================================
     ENHANCE ZEPTO DROPDOWNS
     ======================================== */

  function enhanceSelect(select) {
    const host = select.parentElement;
    const product = getProduct(select);

    if (!host || !product) return;

    select.classList.add(
      'timi-inspo-select'
    );

    let button = host.querySelector(
      ':scope > .timi-surprise-button'
    );

    if (!button) {
      button = document.createElement('button');

      button.type = 'button';
      button.className = 'timi-surprise-button';
      button.textContent = 'Surprise Me ✨';

      button.setAttribute(
        'aria-label',
        'Surprise me with a random embroidery phrase'
      );

      select.after(button);
    }

    // No individual button event listener.
    // The document-level handler below
    // handles all existing and new buttons.

    const enabled = textMode(product);

    host.classList.toggle(
      'timi-inspo-hidden',
      !enabled
    );

    host.classList.toggle(
      'timi-inspo-ready',
      enabled
    );

    host.hidden = !enabled;
    button.hidden = !enabled;

    if (!enabled) {
      showMessage(host);
    }
  }

  function update() {
    document.querySelectorAll(
      '.product-personalizer select.pplr_select'
    ).forEach(select => {
      if (isInspoSelect(select)) {
        enhanceSelect(select);
      }
    });
  }

  function scheduleUpdate() {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  }

  /* ========================================
     INITIALIZATION
     ======================================== */

  function start() {
    injectStyles();
    update();

    // FIX v1.9.1:
    // One delegated handler works even if
    // Zepto rebuilds the dropdown or button.

    document.addEventListener(
      'click',
      event => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        const button = target.closest(
          '.timi-surprise-button'
        );

        if (button) {
          event.preventDefault();

          const product = getProduct(button);

          if (!product) return;

          const select = findLiveInspoSelect(
            product,
            button
          );

          if (!select) {
            scheduleUpdate();
            console.warn(
              '[TIMI Inspo] Active inspiration dropdown not found.'
            );
            return;
          }

          surpriseMe(select);
          return;
        }

        // Recheck when Zepto embroidery
        // mode buttons are clicked.
        const modeButton = target.closest(
          '.product-personalizer .pplr-drop-item'
        );

        if (!modeButton) return;

        ++requestNumber;

        window.setTimeout(update, 0);
        window.setTimeout(update, 100);
        window.setTimeout(update, 250);
        window.setTimeout(update, 500);
      },
      true
    );

    // Manual inspiration dropdown selection.
    document.addEventListener(
      'change',
      event => {
        const select = event.target;

        if (
          !(select instanceof HTMLSelectElement) ||
          !isInspoSelect(select) ||
          !select.value
        ) {
          return;
        }

        fillText(
          select,
          select.value
        );
      }
    );

    // Handle dynamically rebuilt Zepto fields.
    new MutationObserver(records => {
      const relevant = records.some(record => {
        if (
          record.type === 'attributes' &&
          record.target.matches?.(
            '.product-personalizer .pplr-drop-item'
          )
        ) {
          return true;
        }

        if (record.type !== 'childList') {
          return false;
        }

        const target = record.target;

        if (
          target.closest?.(
            '.timi-inspo-message'
          )
        ) {
          return false;
        }

        return [
          ...record.addedNodes
        ].some(node =>
          node.nodeType === 1 &&
          (
            node.matches?.(
              '.product-personalizer, select.pplr_select'
            ) ||
            node.querySelector?.(
              '.product-personalizer, select.pplr_select'
            )
          )
        );
      });

      if (relevant) {
        scheduleUpdate();
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    window.addEventListener(
      'pageshow',
      update
    );

    window.setTimeout(update, 500);
    window.setTimeout(update, 1500);

    console.log(
      'TIMI Inspo Autofill v1.9.1 initialized'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
